import videojs from 'video.js';
import { v4 as uuid } from 'uuid';
import { OverflowMenuItem } from './overflow-menu-item.js';
import './overflow-menu-button.css';
import ExpandLessIcon from '../../../assets/images/three-dots-menu.svg';

const MenuButtonComponent = videojs.getComponent('MenuButton');
let localize;

/**
 * @typedef {Object} OverflowMenuButtonData
 * @property {string} id
 * @property {string} label
 * @property {string} icon
 * @property {boolean} visible
 * @property {function} callback
 * @property {string} [icon]
 * @property {boolean} dismissable
 * @property {Array<OverflowMenuButtonData>} subItems
 */

const _currentMenu = new WeakMap();

/** @type {WeakMap<OverflowMenuButton, OverflowMenuButtonData} */
const _menuItems = new WeakMap();
export class OverflowMenuButton extends MenuButtonComponent {
    constructor(player, options = {}) {
        localize = player.localize?.bind(player);
        super(player, Object.assign({ name: 'OverflowMenuButton' }, options));
        this.addClass('mediasite-player__custom-controlbar-button');
        this.el().style.setProperty('--custom-controlbar-icon', `url("${options.icon || ExpandLessIcon}")`);
        this.controlText(localize('Show more'));
        _menuItems.set(this, []);
        _currentMenu.set(this, null);
    }

    createItems() {
        /** @type {Array<OverflowMenuButtonData>} */
        const menuItems = _menuItems.get(this) ?? [];
        
        return menuItems.reduce((allItems, currentItem) => {
            if (currentItem.visible) {
                const menuItem = new OverflowMenuItem(this.player(), {
					label: currentItem.label,
					icon: currentItem.icon,
					callback: (event) => this.handleItemClick(currentItem.id, event),
					dismissable: currentItem.dismissable
				});
                allItems.push(menuItem);
            }
            return allItems;
        }, []);
    }

    handleItemClick(itemId, event = {}) {
        _menuItems.get(this).find(item => item.id === itemId).callback(event); 
    }

    addMenuItem(label, { id = uuid(), icon, visible = true, callback = () => { }, dismissable = false }) {
        _menuItems.get(this).push({ label, id, icon, visible, callback, dismissable});
        this.update();
        return id;
    }

    updateMenuItem(id, { label, icon, visible, callback, dismissable }) {
        const menuItems = _menuItems.get(this);
        const itemIndex = menuItems.findIndex(item => item.id === id);
        if (itemIndex > -1) {
            const updatedItem = Object.assign({}, menuItems[itemIndex]);
            updatedItem.label = label ?? updatedItem.label;
            updatedItem.icon = icon ?? updatedItem.icon;
            updatedItem.visible = visible ?? updatedItem.visible;
            updatedItem.callback = callback ?? updatedItem.callback;
            updatedItem.dismissable = (dismissable !== undefined && dismissable) ?? updatedItem.dismissable;

            const newMenuItems = menuItems.slice();
            newMenuItems.splice(itemIndex, 1, updatedItem);
            _menuItems.set(this, newMenuItems);
            this.update();
        }
    }

    removeMenuItem(itemId) {
        if (this.hasItem(itemId)) {
            const menuItems = _menuItems.get(this);
            menuItems.splice(menuItems.findIndex(item => item.id === itemId), 1);
            this.update();

            if (menuItems.length === 0) {
                this.setTimeout(() => this.unpressButton(), 0);
            }
        }
    }

    hasItem(itemId) {
        return _menuItems.get(this).some(item => item.id === itemId);
    }

    isItemVisible(itemId) {
        return _menuItems.get(this).find(item => item.id === itemId)?.visible;
    }

    showMenuItem(itemId) {
        if (this.hasItem(itemId)) {
            const item = _menuItems.get(this).find(item => item.id === itemId);
            item.visible = true;
            this.update();
        }
    }

    hideMenuItem(itemId) {
        if (this.hasItem(itemId)) {
            const item = _menuItems.get(this).find(item => item.id === itemId);
            item.visible = false;
            this.update();
        }
    }
}
